<template>
  <main>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <form-select @change="load" :model="filterFormModel" attribute="group" class="mr-1" :options="[
                            {value:'day',text:$t('byDays')},
                            {value:'week',text:$t('byWeeks')},
                            {value:'month',text:$t('byMonths')}
                            ]">
            </form-select>
            <time-range-picker :model="filterFormModel" :label="$t('time')" :defaultShortcut="3"
                               class="mr-1"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1">{{ $t('search') }}</button>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="tile shadow">
          <h3 class="tile-title">{{ $t('clientByCategories') }}</h3>
          <apexchart :height="300" :options="chartOptions" :series="chartData"></apexchart>
        </div>
      </div>
      <div class="col-12">
        <div class="tile">
          <div class="table-responsive c-template-table">
            <table class="table table-hover table-bordered">
              <thead>
              <tr>
                <th rowspan="2"></th>
                <th colspan="2" class="text-center align-middle" v-for="(item, index) in data" :key="index">{{
                    item.name
                  }}
                </th>
              </tr>
              <tr>
                <template v-for="(item, index) in data">
                  <td class="text-center text-blue align-middle" :key="`col1-${index}`">
                    <b>{{ $t('newPaid') }}</b>
                  </td>
                  <td class="text-center text-blue align-middle" :key="`col2-${index}`">
                    <b>{{ $t('paid') }}</b>
                  </td>
                </template>
              </tr>
              </thead>
              <tbody>
              <tr class="bg-success">
                <td><b class="text-uppercase">{{ $t('total') }}</b></td>
                <template v-for="(item, index) in data">
                  <td class="text-center text-blue align-middle" :key="`colSum1-${index}`">
                    {{ getColumnSum(index, 'newPaid') | numFormat }}
                  </td>
                  <td class="text-center text-blue align-middle" :key="`colSum2-${index}`">
                    {{ getColumnSum(index, 'y') | numFormat }}
                  </td>
                </template>
              </tr>
              <tr class="bg-warning">
                <td><b class="text-uppercase">{{ $t('average') }}</b></td>
                <template v-for="(item, index) in data">
                  <td class="text-center text-blue align-middle" :key="`colAvg1-${index}`">
                    {{ getColumnAverage(index, 'newPaid') | numFormat }}
                  </td>
                  <td class="text-center text-blue align-middle" :key="`colAvg2-${index}`">
                    {{ getColumnAverage(index, 'y') | numFormat }}
                  </td>
                </template>
              </tr>
              <tr v-for="(rowData, rowIndex) in getMaxDataLength()" :key="rowIndex">
                <td><b>{{ getXValue(rowIndex) }}</b></td>
                <template v-for="(item, index) in data">
                  <td class="text-center text-blue align-middle" :key="`colItem1-${index}`">
                    {{ getValue(item, rowIndex, 'newPaid') | numFormat }}
                  </td>
                  <td class="text-center text-blue align-middle" :key="`colItem2-${index}`">
                    {{ getValue(item, rowIndex, 'y') | numFormat }}
                  </td>
                </template>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import moment from 'moment';
import TimeRangePicker from "../../components/TimeRangePicker";
import VueApexCharts from 'vue-apexcharts';

export default {
  components: {
    TimeRangePicker,
    'apexchart': VueApexCharts
  },
  data() {
    return {
      filterFormModel: {
        group: 'day'
      },
      loading: true,
      data: [],
      chartOptions: {
        chart: {
          type: 'line'
        }
      },
      chartData: []
    }
  },
  methods: {
    load() {
      let loader = this.$loading.show();
      this.loading = true;
      this.$service.get('/report/client-by-category', {
        params: this.filterFormModel
      }).then(response => {
        this.data = [];
        if (!this.filterFormModel.from || !this.filterFormModel.to) {
          this.filterFormModel.to = moment(Date.now(), 'x').endOf('day').format('x');
          this.filterFormModel.from = moment(Date.now(), 'x').subtract('29', 'day').startOf('day').format('x');
        }

        this.data = response.data
        const baseColors = ['#FF4560', '#008FFB', '#00E396', '#FEB019', '#775DD0', '#546E7A', '#26A69A', '#D10CE8'];
        const colors = this.data.map((_, index) => {
          if (index < baseColors.length) {
            return baseColors[index];
          } else {
            return this.generateRandomColor();
          }
        });
        this.chartOptions = {
          colors: colors,
          chart: {
            type: 'line',
            zoom: {
              enabled: false
            }
          },
          stroke: {
            curve: 'straight'
          },
          markers: {
            size: 1
          },
          yaxis: {
            labels: {
              formatter: function (value) {
                return new Intl.NumberFormat().format(value)
              }
            }
          }
        }

        this.chartData = this.data.map(item => {
          return {
            ...item,
            data: [...item.data].reverse()
          }
        })
      }).finally(() => {
        loader.hide();
        this.loading = false;
      });
    },
    generateRandomColor() {
      const letters = '0123456789ABCDEF';
      let color = '#';
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    },
    getColumnSum(columnIndex, payType) {
      let sum = 0
      this.data[columnIndex].data.forEach(item => {
        sum += item[payType]
      })
      return sum
    },
    getColumnAverage(columnIndex, payType) {
      const columnCount = this.data[0].data.length
      return columnCount === 0 ? 0 : this.getColumnSum(columnIndex, payType) / columnCount
    },
    getMaxDataLength() {
      return Math.max(...this.data.map(item => item.data.length))
    },
    getXValue(rowIndex) {
      return this.data[0].data[rowIndex] ? this.data[0].data[rowIndex].x : ''
    },
    getValue(item, rowIndex, payType) {
      return item.data[rowIndex] ? item.data[rowIndex][payType] : ''
    },
  },
  mounted() {
    this.load()
  },
  i18n: {
    messages: {
      en: {
        clientByCategories: "Customers by categories",
        byDays: 'By days',
        byWeeks: 'By weeks',
        byMonths: 'By months',
        paid: "Paid",
        newPaid: 'New paid'
      },
      vi: {
        clientByCategories: "Khách hàng theo từng ngành",
        byDays: 'Theo ngày',
        byWeeks: 'Theo tuần',
        byMonths: 'Theo tháng',
        paid: "Đang trả phí",
        newPaid: 'Trả phí mới'
      }
    }
  }
};
</script>